/**
 * Persistent banner for anonymous users to sign up.
 *
 * Shows immediately when the user is anonymous. Can be dismissed, but
 * re-appears on next page load (tracked per-session via sessionStorage).
 * Renders through the shared {@link Banner} so its layout, buttons, and
 * dismiss placement stay consistent with every other banner.
 *
 * **Mutual exclusion with the guest budget nudge:** when a guest has used
 * today's free AI budget, {@link UsageNudge} shows the red "…budget for
 * guests. It resets… or you can log in for more free AI" banner — which
 * already carries the same Sign up + Log in call to action. Stacking the blue
 * "sign up or log in to keep your work" reminder beside it is redundant, so
 * this banner consults the same live usage rollup and stays hidden while the
 * AI budget is over. It also waits for the first fetch to settle before
 * showing, so an over-budget guest never sees it flash in and then vanish.
 */

import { useState } from 'react'

import { useTranslation } from '@molecule/app-react'

import { useAccountUsage } from '../hooks/useAccountUsage.js'
import { nudgeStateFromUsage } from '../utilities/usage-utilities.js'
import { Banner } from './Banner.js'

const STORAGE_KEY = 'mol_guest_reminder_dismissed'

export function GuestReminder({ isAnonymous }: { isAnonymous: boolean }) {
  const { t } = useTranslation()
  const { usage, settled: usageSettled } = useAccountUsage()
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(STORAGE_KEY) === '1')

  if (!isAnonymous || dismissed) return null
  // Hold until the usage fetch resolves so an over-budget guest never sees this
  // flash before the red budget nudge takes over (and hides it).
  if (!usageSettled) return null
  // The guest AI budget is spent → the red "budget for guests" nudge owns this
  // moment and already offers Sign up / Log in. A second reminder is redundant.
  const aiCostOver = nudgeStateFromUsage(usage).metrics.some(
    (m) => m.key === 'aiCost' && m.state === 'over',
  )
  if (aiCostOver) return null

  const dismiss = () => {
    setDismissed(true)
    sessionStorage.setItem(STORAGE_KEY, '1')
  }

  return (
    <Banner
      tone="info"
      variant="strip"
      role="alert"
      molId="guest-reminder"
      message={t('guest.reminder.message', undefined, {
        defaultValue: 'Sign up or log in to keep your work. Guest sessions expire after 72 hours.',
      })}
      actions={[
        // Canonical CTA colors (DESIGN.md), mirroring the auth page: Sign up
        // primary, Log in success.
        {
          label: t('guest.reminder.signUp', undefined, { defaultValue: 'Sign up' }),
          href: '/signup',
          color: 'primary',
          molId: 'guest-reminder-signup',
        },
        {
          label: t('guest.reminder.logIn', undefined, { defaultValue: 'Log in' }),
          href: '/login',
          color: 'success',
          molId: 'guest-reminder-login',
        },
      ]}
      onDismiss={dismiss}
      dismissMolId="guest-reminder-dismiss"
    />
  )
}
